import { auth, init, subscription } from 'cubeforall';
import type {
  CubeActiveAccount,
  CubeSdkConfig,
  CubeSubscriptionResult,
  CubeSsoLoginPayload,
  CubeSsoLoginResponse,
} from '@/types/cube-sso';
import { apiUrl } from '@constants/url';
import { runtimeEnv } from '@/config/runtime';
import axiosInstance from '@libs/axios';
import { CubeSubscriptionInactiveError } from './cube-sso-errors';

export { CubeSubscriptionInactiveError };

let initPromise: Promise<void> | null = null;

export const CUBE_AUTH_SOURCE = 'cubeforall';
export const CUBE_EXTERNAL_PROVIDER = 'ccn';

const CUBE_POLICY_SIGNUP_SIGNIN = 'B2C_1A_SIGNUP_SIGNINNEWUSERSYN';
const CUBE_POLICY_FORGOT_PASSWORD = 'b2c_1a_passwordreset';

const DEFAULT_AUTHORITY_URLS = {
  sandbox: 'https://ccnssoppd.b2clogin.com/ccnssoppd.onmicrosoft.com',
  prod: 'https://ccnsso.b2clogin.com/ccnsso.onmicrosoft.com',
} as const;

const normalizeCubeEnvironment = (value?: string) => {
  const normalized = value?.toLowerCase();

  if (normalized === 'sandbox' || normalized === 'ppd') {
    return 'sandbox';
  }

  return 'prod';
};

const normalizeLoginType = (value?: string) => {
  const normalized = value?.toLowerCase();

  if (normalized === 'redirect' || normalized === 'silent' || normalized === 'popup') {
    return normalized;
  }

  return 'popup';
};

// CUBE settings are runtime config (Infisical → pod env → /config.js) with
// the build-time VITE_* values as fallback; see src/config/runtime.ts.
export const getCubeConfig = (): CubeSdkConfig => {
  const environment = normalizeCubeEnvironment(runtimeEnv('VITE_CUBE_ENVIRONMENT'));

  return {
    serviceId: runtimeEnv('VITE_CUBE_SERVICE_ID'),
    clientId: runtimeEnv('VITE_CUBE_CLIENT_ID'),
    environment,
    loginType: normalizeLoginType(runtimeEnv('VITE_CUBE_LOGIN_TYPE')),
    authorityUrl: runtimeEnv('VITE_CUBE_AUTHORITY_URL') || DEFAULT_AUTHORITY_URLS[environment],
    signupSigninPolicy: import.meta.env.VITE_CUBE_SIGNUP_SIGNIN_POLICY || CUBE_POLICY_SIGNUP_SIGNIN,
    forgotPasswordPolicy: import.meta.env.VITE_CUBE_FORGOT_PASSWORD_POLICY || CUBE_POLICY_FORGOT_PASSWORD,
    pollIntervalMilliseconds: runtimeEnv('VITE_CUBE_POLL_INTERVAL_MS') || '0',
    redirectUrl: runtimeEnv('VITE_CUBE_REDIRECT_URL') || window.location.origin,
  };
};

/**
 * CCN's JARVIS product page on CUBEforall, where a company subscribes.
 * VITE_CUBE_PRODUCT_URL wins; sandbox falls back to the page CCN gave us
 * (2026-10-15). Production has no default until CCN publishes its page, so
 * an unsubscribed user there keeps the plain "inactive" error.
 */
const DEFAULT_PRODUCT_URLS: Partial<Record<'sandbox' | 'prod', string>> = {
  sandbox: 'https://sandbox.cubeforall.com/products/forwarders-shippers/jarvis-sub/',
};

export const getCubeProductPageUrl = (): string | undefined =>
  runtimeEnv('VITE_CUBE_PRODUCT_URL') ||
  DEFAULT_PRODUCT_URLS[normalizeCubeEnvironment(runtimeEnv('VITE_CUBE_ENVIRONMENT'))];

/**
 * Where a failed sign-in should send the user: CCN's product page when the
 * failure is "no active JARVIS subscription" and that page is known, else
 * undefined (show the error as before). CCN's SSO guide (v1.2 §4.3): show a
 * message, then redirect to the partner product page.
 */
export const productPageForLoginError = (error: unknown): string | undefined =>
  error instanceof CubeSubscriptionInactiveError ? getCubeProductPageUrl() : undefined;

/** How long the "not subscribed" message stays before the product page opens. */
export const PRODUCT_REDIRECT_DELAY_MS = 4000;

function assertCubeConfig(
  config: CubeSdkConfig
): asserts config is CubeSdkConfig & Required<Pick<CubeSdkConfig, 'serviceId' | 'clientId'>> {
  const missingKeys = ['serviceId', 'clientId'].filter((key) => !config[key as keyof CubeSdkConfig]);

  if (missingKeys.length > 0) {
    throw new Error(`CUBE SSO configuration is incomplete: ${missingKeys.join(', ')}`);
  }
}

export const initializeCubeSdk = async (callbacks?: {
  onSuccess?: (result: unknown) => void;
  onError?: (error: unknown) => void;
}) => {
  if (typeof window === 'undefined') {
    throw new Error('CUBE SSO is only available in the browser');
  }

  const config = getCubeConfig();
  assertCubeConfig(config);

  if (!initPromise) {
    initPromise = Promise.resolve(
      init(
        config.serviceId,
        config.clientId,
        config.environment,
        config.loginType,
        config.authorityUrl,
        config.signupSigninPolicy,
        config.forgotPasswordPolicy,
        config.pollIntervalMilliseconds,
        config.redirectUrl,
        callbacks?.onSuccess,
        callbacks?.onError
      )
    ).then(() => undefined);
  }

  return initPromise;
};

export const triggerCubeLogin = async () => {
  await initializeCubeSdk();
  return auth.signIn();
};

/** sessionStorage key: where to send a CCN user once their session exists. */
export const CCN_RETURN_TO_KEY = 'ccn_return_to';

const looksLikeJwt = (value: unknown): value is string =>
  typeof value === 'string' && value.split('.').length === 3 && value.length > 40;

/** The `exp` claim of a JWT in seconds, or 0 when it cannot be read. */
const jwtExp = (token: string): number => {
  const claims = decodeBase64Json(token.split('.')[1]);
  const exp = Number(claims?.exp);
  return Number.isFinite(exp) ? exp : 0;
};

/**
 * The raw Azure B2C ID token of the signed-in CUBE account.
 *
 * Candidates are the active account's `idToken` and every ID token in
 * MSAL's localStorage cache for our client id (keys
 * `<homeAccountId>-<env>-idtoken-<clientId>-<realm>-`, value `{secret}`).
 * The one that expires LAST wins. The SDK's `getActiveAccount()` returns a
 * snapshot taken when the page loaded, so after a redirect sign-in it still
 * carries the old, expired token while the new one sits in the cache; the
 * newest token always belongs to the most recent sign-in.
 * The backend verifies this token, so the login no longer trusts the
 * email/name the browser claims.
 */
export const getCubeIdToken = (activeAccount?: CubeActiveAccount | null): string | undefined => {
  const account = activeAccount ?? (auth.getActiveAccount?.() as CubeActiveAccount | null | undefined) ?? null;
  type Candidate = { token: string; exp: number; expiresOn: number };
  const candidates: Candidate[] = [];
  if (looksLikeJwt(account?.idToken)) {
    candidates.push({ token: account.idToken, exp: jwtExp(account.idToken), expiresOn: Number.POSITIVE_INFINITY });
  }

  if (typeof localStorage !== 'undefined') {
    const clientId = (runtimeEnv('VITE_CUBE_CLIENT_ID') || '').toLowerCase();
    try {
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (!key || !key.toLowerCase().includes('-idtoken-')) continue;
        if (clientId && !key.toLowerCase().includes(clientId)) continue;
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        const entry = JSON.parse(raw) as { secret?: unknown; expiresOn?: unknown };
        if (!looksLikeJwt(entry.secret)) continue;
        candidates.push({ token: entry.secret, exp: jwtExp(entry.secret), expiresOn: Number(entry.expiresOn) || 0 });
      }
    } catch {
      // unreadable cache: fall through with what we have
    }
  }

  let best: Candidate | undefined;
  for (const c of candidates) {
    if (!best || c.exp > best.exp || (c.exp === best.exp && c.expiresOn > best.expiresOn)) best = c;
  }
  return best?.token;
};

const decodeBase64Json = (value: string): Record<string, unknown> | null => {
  try {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
  } catch {
    return null;
  }
};

/** CCN's account snapshot inside the ID token (`uifo` claim). */
export type CubeTokenAccount = {
  cubeId?: string | null;
  userPlanId?: string | null;
  pricePlanName?: string | null;
  status?: boolean;
  statusSubscription?: string | null;
  company?: { name?: string | null; companyCubeId?: string | null } | null;
};

/**
 * Reads the ID token's claims WITHOUT verifying them. Good enough to fill a
 * form or word an error message; never a security decision — the backend
 * verifies the same token against CCN's keys and trusts only that.
 */
export const readCubeIdTokenClaims = (
  idToken?: string
): { claims: Record<string, unknown>; account: CubeTokenAccount | null } | null => {
  if (!looksLikeJwt(idToken)) return null;
  const claims = decodeBase64Json(idToken.split('.')[1]);
  if (!claims) return null;
  const uifo = typeof claims.uifo === 'string' ? decodeBase64Json(claims.uifo) : null;
  return { claims, account: (uifo as CubeTokenAccount | null) ?? null };
};

/** The cached CUBE ID token has run out; only a new sign-in can replace it. */
export class CubeTokenExpiredError extends Error {
  constructor() {
    super('CUBE session expired');
    this.name = 'CubeTokenExpiredError';
  }
}

/** True when the token's `exp` is in the past (or within the next minute). */
export const isCubeIdTokenExpired = (idToken: string | undefined, now = Date.now()): boolean => {
  if (!looksLikeJwt(idToken)) return false;
  const claims = decodeBase64Json(idToken.split('.')[1]);
  const exp = typeof claims?.exp === 'number' ? claims.exp : Number(claims?.exp);
  if (!Number.isFinite(exp)) return false;
  return exp * 1000 <= now + 60_000;
};

/**
 * The ID token to send to the backend, refreshed when the cached one has
 * expired. MSAL keeps the account (and Microsoft keeps its cookie) long after
 * the hour-long ID token runs out, so a returning user otherwise hands the
 * backend a stale token and is refused. The SDK's guard runs a silent
 * renewal; when that cannot (third-party cookies blocked, session gone) the
 * caller has to start a sign-in.
 */
const freshCubeIdToken = async (): Promise<string | undefined> => {
  let idToken = getCubeIdToken();
  if (!idToken || !isCubeIdTokenExpired(idToken)) return idToken;
  try {
    await auth.guard();
  } catch (error) {
    console.warn('[CUBE SSO] silent token renewal failed:', error);
  }
  idToken = getCubeIdToken();
  if (idToken && isCubeIdTokenExpired(idToken)) {
    throw new CubeTokenExpiredError();
  }
  return idToken;
};

export const getCubeIdentityBundle = async () => {
  await initializeCubeSdk();
  const idToken = await freshCubeIdToken();
  const activeAccount = (auth.getActiveAccount?.() as CubeActiveAccount | null | undefined) ?? null;

  // With an ID token in hand the SDK's subscription lookup is redundant: CCN
  // embeds the same account snapshot in the token (`uifo`), and the backend
  // reads it from the VERIFIED token. The lookup itself is a browser call to
  // <cube>/register that CCN's gateway blocks by CORS for partner origins,
  // which used to surface here as a bare "Network Error" after a good sign-in.
  let subscriptionResult: CubeSubscriptionResult | null = null;
  if (!idToken) {
    subscriptionResult =
      ((await subscription.checkSubscription()) as CubeSubscriptionResult | null | undefined) ?? null;
  }

  return {
    activeAccount,
    subscription: subscriptionResult,
    idToken,
  };
};

const pickString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim() !== '') return value.trim();
  }
  return undefined;
};

/**
 * The body of POST /auth/cube-sso/login for the signed-in CUBE account.
 *
 * Token path (normal): identity and subscription are taken from the token's
 * own claims for display, the raw token rides along, and the BACKEND decides
 * — including "subscription inactive", which comes back as its error message.
 * Legacy path (no token available): the SDK lookup result is required and an
 * inactive subscription is refused here, as before.
 */
export const buildCubeSsoPayload = async (): Promise<CubeSsoLoginPayload> => {
  const { activeAccount, subscription: sdkSubscription, idToken } = await getCubeIdentityBundle();
  const token = readCubeIdTokenClaims(idToken);
  const claims = token?.claims ?? {};
  const account = token?.account ?? null;
  const emails = Array.isArray(claims.emails) ? claims.emails : [];

  const email = pickString(activeAccount?.email, emails[0], claims.email, activeAccount?.username);
  const fullName = pickString(
    activeAccount?.fullName,
    activeAccount?.name,
    activeAccount?.displayName,
    claims.name,
    email
  );
  const externalSubject = pickString(
    claims.extension_CubeUserID,
    activeAccount?.sub,
    claims.sub,
    activeAccount?.oid,
    activeAccount?.userId
  );

  if (!email) throw new Error('CUBE SSO login failed: account email is missing');
  if (!fullName) throw new Error('CUBE SSO login failed: account full name is missing');

  const base = {
    email,
    fullName,
    authSource: CUBE_AUTH_SOURCE,
    externalProvider: CUBE_EXTERNAL_PROVIDER,
    externalSubject,
    idToken,
    rawClaims: activeAccount ? { ...activeAccount } : undefined,
  };

  if (token) {
    return {
      ...base,
      subscription: {
        isActive: account?.status === true,
        status: pickString(account?.statusSubscription) ?? 'FROM_TOKEN',
        plan: pickString(account?.pricePlanName),
        raw: account ?? undefined,
      },
      companyName: pickString(claims.extension_Company, account?.company?.name, activeAccount?.companyName),
      companyId: pickString(account?.company?.companyCubeId, activeAccount?.companyId),
    };
  }

  const isActive = sdkSubscription?.isActive ?? sdkSubscription?.active ?? false;
  if (!isActive) throw new Error('Access denied: your CUBEforall subscription is inactive');
  return {
    ...base,
    subscription: {
      isActive: true,
      status: pickString(sdkSubscription?.status) ?? 'ACTIVE',
      plan: pickString(sdkSubscription?.plan),
      raw: sdkSubscription ?? undefined,
    },
    companyName: pickString(sdkSubscription?.companyName, activeAccount?.companyName),
    companyId: pickString(sdkSubscription?.companyId, activeAccount?.companyId),
  };
};

/**
 * Whether a CUBE account is already signed in in this browser (an existing
 * B2C session, or the return leg of a redirect sign-in).
 */
export const getCubeActiveAccount = async (): Promise<CubeActiveAccount | null> => {
  await initializeCubeSdk();
  return (auth.getActiveAccount?.() as CubeActiveAccount | null | undefined) ?? null;
};

/**
 * Register the SDK's post-redirect hook. In redirect login mode MSAL
 * finishes the sign-in when the page reloads on VITE_CUBE_REDIRECT_URL and
 * calls this once the account is available.
 */
export const onCubeRedirectResult = async (callback: (account: unknown) => void) => {
  await initializeCubeSdk();
  auth.handleLoginRedirect?.(callback);
};

export const signOutCube = async () => {
  try {
    await initializeCubeSdk();
    await auth.signOut?.();
  } catch (error) {
    console.warn('[CUBE SSO] sign-out skipped:', error);
  }
};

export const guardCubeSession = async () => {
  await initializeCubeSdk();

  try {
    await auth.guard();
    return true;
  } catch {
    return false;
  }
};

export const exchangeCubeSsoLogin = async (payload: CubeSsoLoginPayload) => {
  const response = await axiosInstance.post<CubeSsoLoginResponse>(apiUrl.cubeSso.login, payload);
  return response.data;
};
